import { motion } from 'framer-motion';
import imgVibeCoding from '@/assets/img-vibe-coding.jpg';
import imgCollaboration from '@/assets/img-collaboration.jpg';
import imgAutomation from '@/assets/img-automation.jpg';
import imgMultiAgent from '@/assets/img-multi-agent.jpg';
import imgSkills from '@/assets/img-skills.jpg';
import imgSecureSharing from '@/assets/img-secure-sharing.jpg';
import bgTech2 from '@/assets/bg-tech-2.jpg';

const features = [
  {
    image: imgVibeCoding,
    title: 'Agentic Workspaces',
    body: 'Launch secure sessions where AI agents and humans collaborate with shared context and instant onboarding.',
    roi: 'From idea to execution in seconds.',
  },
  {
    image: imgMultiAgent,
    title: 'Multi-Agent Orchestration',
    body: 'Route tasks to specialized AI agents for planning, building, QA, and review — running in parallel.',
    roi: 'Multiply your team output.',
  },
  {
    image: imgSkills,
    title: 'Skill Injection',
    body: 'Attach domain knowledge, internal playbooks, and coding standards so agents work your way.',
    roi: 'Consistent quality at scale.',
  },
  {
    image: imgAutomation,
    title: 'Autonomous Task Execution',
    body: 'Queue complex workflows — refactors, migrations, docs, tests — with built-in approval checkpoints.',
    roi: 'Automate the busywork.',
  },
  {
    image: imgCollaboration,
    title: 'Real-Time Collaboration',
    body: 'Invite clients or teammates with scoped access, time limits, and complete activity trails.',
    roi: 'Fast teamwork with control.',
  },
  {
    image: imgSecureSharing,
    title: 'Governance & Audit Trails',
    body: 'Every agent action is logged from prompt to delivery — built for security teams and compliance.',
    roi: 'Enterprise-ready by default.',
  },
];

const FeaturePillars = () => {
  return (
    <section id="features" className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={bgTech2} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      </div>
      <div className="mx-auto max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            The agentic AI platform for modern teams
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Everything you need to deploy, orchestrate, and govern autonomous AI agents across your organization.
          </p>
        </motion.div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass-card-hover rounded-2xl overflow-hidden"
            >
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              <div className="p-8">
                <h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{feature.body}</p>
                <p className="mt-3 text-xs font-medium text-primary">{feature.roi}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturePillars;
